import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Divider,
  InputAdornment,
  Stack,
  CircularProgress
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Payment as PaymentIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
// import { format } from 'date-fns';

const PaymentManagementDialog = ({
  open,
  onClose,
  transaction,
  customer,
  onPaymentComplete,
  recordPayment
}) => {
  // const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [paymentModes, setPaymentModes] = useState({
    cash: '',
    online: '',
    cheque: '',
    other: ''
  });
  const [paymentNote, setPaymentNote] = useState('');
  const [remainingBalance, setRemainingBalance] = useState(0);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [originalAmount, setOriginalAmount] = useState(0);

  // In PaymentManagementDialog.js, update the useEffect to properly handle the initial payment:

  useEffect(() => {
    if (transaction) {
      const balance = transaction.transactionDetails?.balanceDue ||
        (transaction.summary?.grandTotal - transaction.summary?.paymentDone);
      const original = transaction.transactionDetails?.originalBillAmount ||
        transaction.summary?.grandTotal || 0;
      setRemainingBalance(Number(balance));
      setOriginalAmount(Number(original));

      // Get payment history - ensure it exists
      const history = transaction.paymentDetails?.paymentHistory || [];

      // If there's payment done but no history entries, create a synthetic history entry
      const paymentDone = transaction.summary?.paymentDone || 0;
      if (paymentDone > 0 && history.length === 0) {
        // This handles backward compatibility for old transactions without payment history
        const syntheticPayment = {
          id: `synthetic_${Date.now()}`,
          date: transaction.date,
          amount: paymentDone,
          paymentModes: {
            cash: transaction.paymentDetails?.cash || 0,
            online: transaction.paymentDetails?.online || 0,
            cheque: transaction.paymentDetails?.cheque || 0,
            other: transaction.paymentDetails?.other || 0,
            advanceBreakup: transaction.paymentDetails?.advancePayment?.breakup || {}
          },
          note: "Initial payment (historical)",
          recordedBy: transaction.metaDetails?.handledBy || "System"
        };
        setPaymentHistory([syntheticPayment]);
      } else {
        setPaymentHistory(history);
      }
    }
  }, [transaction]);

  const calculateTotalPayment = () => {
    return Object.values(paymentModes).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  };

  const handlePaymentModeChange = (mode, value) => {
    const newPaymentModes = { ...paymentModes, [mode]: value };
    setPaymentModes(newPaymentModes);

    // const total = Object.values(newPaymentModes).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
    // setPaymentAmount(total);
  };

  const handleSubmitPayment = async () => {
    const totalPayment = calculateTotalPayment();

    if (totalPayment <= 0) {
      alert('Please enter payment amount');
      return;
    }

    if (totalPayment > remainingBalance) {
      alert(`Payment cannot exceed remaining balance of ₹${remainingBalance.toFixed(2)}`);
      return;
    }

    setLoading(true);

    // Format date as dd/mm/yyyy without leading apostrophe
    const day = paymentDate.getDate();
    const month = paymentDate.getMonth() + 1;
    const year = paymentDate.getFullYear();
    const paymentDateValue = `'${day}/${month}/${year}`;

    console.log("Formatted payment date:", paymentDateValue);

    // Create the payment data structure
    const paymentData = {
      amount: totalPayment,
      date: paymentDateValue,
      paymentModes: {
        cash: parseFloat(paymentModes.cash) || 0,
        online: parseFloat(paymentModes.online) || 0,
        cheque: parseFloat(paymentModes.cheque) || 0,
        other: parseFloat(paymentModes.other) || 0,
        advanceBreakup: {}
      },
      note: paymentNote,
      recordedBy: "Cashier"
    };

    console.log("Submitting payment data:", paymentData);

    try {
      const result = await recordPayment(transaction.id, paymentData);

      if (result.success) {
        // Reset form
        setPaymentModes({
          cash: '',
          online: '',
          cheque: '',
          other: ''
        });
        setPaymentNote('');
        setPaymentDate(new Date());

        onPaymentComplete(result.data);
        onClose();
      } else {
        alert(result.message || 'Failed to record payment');
      }
    } catch (error) {
      console.error("Payment error:", error);
      alert(error.message || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  // const formatDate = (dateString) => {
  //   return format(new Date(dateString), 'dd/MM/yyyy HH:mm');
  // };

  const totalPaid = paymentHistory.reduce((sum, p) => sum + p.amount, 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <PaymentIcon color="primary" />
            <Typography variant="h6">Payment Management</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Bill Information */}
          <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2" gutterBottom>Bill Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Bill Number: <strong>{transaction?.transactionDetails?.billNo}</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Bill Date: <strong>{transaction?.date}</strong>
                </Typography>
                {customer && (
                  <Typography variant="body2" color="textSecondary">
                    Customer: <strong>{customer.name}</strong>
                  </Typography>
                )}
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Total Amount: <strong>₹{originalAmount.toFixed(2)}</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Paid: <strong>₹{totalPaid.toFixed(2)}</strong>
                </Typography>
                <Typography variant="body2" color="error">
                  Remaining Balance: <strong>₹{remainingBalance.toFixed(2)}</strong>
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Payment Progress */}
          <Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Payment Progress
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{
                  height: 8,
                  bgcolor: 'grey.200',
                  borderRadius: 4,
                  overflow: 'hidden'
                }}>
                  <Box sx={{
                    width: `${(totalPaid / originalAmount) * 100}%`,
                    height: '100%',
                    bgcolor: totalPaid >= originalAmount ? 'success.main' : 'primary.main',
                    transition: 'width 0.3s ease'
                  }} />
                </Box>
              </Box>
              <Typography variant="body2" fontWeight="bold">
                {((totalPaid / originalAmount) * 100).toFixed(1)}%
              </Typography>
            </Box>
          </Box>

          {/* Payment History */}
          {paymentHistory.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom display="flex" alignItems="center" gap={1}>
                <HistoryIcon fontSize="small" />
                Payment History ({paymentHistory.length} payments)
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Payment Modes</TableCell>
                      <TableCell>Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentHistory.map((payment, index) => (
                      <TableRow key={payment.id} sx={{
                        bgcolor: index === paymentHistory.length - 1 ? 'action.hover' : 'inherit'
                      }}>
                        <TableCell>{payment?.date?.toString()?.slice(1)}</TableCell>
                        <TableCell align="right">
                          <strong>₹{payment.amount.toFixed(2)}</strong>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {payment.paymentModes.cash > 0 && (
                              <Chip size="small" label={`Cash: ₹${payment.paymentModes.cash}`} />
                            )}
                            {payment.paymentModes.online > 0 && (
                              <Chip size="small" label={`Online: ₹${payment.paymentModes.online}`} />
                            )}
                            {payment.paymentModes.cheque > 0 && (
                              <Chip size="small" label={`Cheque: ₹${payment.paymentModes.cheque}`} />
                            )}
                            {payment.paymentModes.other > 0 && (
                              <Chip size="small" label={`Other: ₹${payment.paymentModes.other}`} />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>{payment.note}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* New Payment Section */}
          {remainingBalance > 0 && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" gutterBottom>Record New Payment</Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <DatePicker
                      label="Payment Date"
                      value={paymentDate}
                      onChange={setPaymentDate}
                      format="dd/MM/yyyy"
                      slotProps={{ textField: { fullWidth: true, size: "small" } }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary" gutterBottom>
                      Payment Details
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label="Cash"
                      value={paymentModes.cash}
                      onChange={(e) => handlePaymentModeChange('cash', e.target.value)}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label="Online"
                      value={paymentModes.online}
                      onChange={(e) => handlePaymentModeChange('online', e.target.value)}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label="Cheque"
                      value={paymentModes.cheque}
                      onChange={(e) => handlePaymentModeChange('cheque', e.target.value)}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label="Other"
                      value={paymentModes.other}
                      onChange={(e) => handlePaymentModeChange('other', e.target.value)}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="Note (Optional)"
                      value={paymentNote}
                      onChange={(e) => setPaymentNote(e.target.value)}
                      fullWidth
                      size="small"
                      multiline
                      rows={2}
                      placeholder="e.g., Partial payment, Cheque number, etc."
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Alert severity="info">
                      <strong>Payment Summary:</strong> ₹{calculateTotalPayment().toFixed(2)} |
                      Remaining after payment: ₹{(remainingBalance - calculateTotalPayment()).toFixed(2)}
                    </Alert>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}

          {remainingBalance === 0 && (
            <Alert severity="success" icon={<CheckCircleIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <ReceiptIcon />
                <Typography>This bill is fully paid! No further payments needed.</Typography>
              </Box>
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {remainingBalance > 0 && (
          <Button
            onClick={handleSubmitPayment}
            variant="contained"
            color="primary"
            disabled={calculateTotalPayment() <= 0 || calculateTotalPayment() > remainingBalance || loading}
            startIcon={loading ? <CircularProgress size={20} /> : <PaymentIcon />}
          >
            {loading ? 'Processing...' : 'Record Payment'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PaymentManagementDialog;